import React, { useMemo, useRef } from 'react';
import type { BackgroundThemeProps } from '../backgroundThemes';
import { useCanvasLoop } from '../useCanvasLoop';

type Size = { width: number; height: number };

interface Star {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  phase: number;
  speed: number;
}

interface ShootingStar {
  active: boolean;
  x: number;
  y: number;
  unitX: number;
  unitY: number;
  speed: number;
  age: number;
  life: number;
  tail: number;
  lineWidth: number;
}

const DESKTOP_STAR_COUNT = 70;
const MOBILE_STAR_COUNT = 35;
const DESKTOP_SHOOTING_COUNT = 3;
const MOBILE_SHOOTING_COUNT = 2;
const MAX_DELTA_MS = 250;
const TWO_PI = Math.PI * 2;

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);
const rgba = (red: number, green: number, blue: number, alpha: number) =>
  `rgba(${red}, ${green}, ${blue}, ${alpha})`;

export const FallingCometsBackground: React.FC<BackgroundThemeProps> = ({ tone, activity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null!);

  const renderer = useMemo(() => {
    let stars: Star[] = [];
    let shootingStars: ShootingStar[] = [];
    let elapsedSeconds = 0;
    let spawnAccumulator = 0;
    let nextSpawnDelay = randomBetween(1.1, 2.5);
    const isDark = tone === 'dark';
    const starColor = isDark ? 'rgb(255, 255, 255)' : 'rgb(30, 64, 175)';
    const tailColor = isDark ? 'rgba(125, 211, 252, 0)' : 'rgba(99, 102, 241, 0)';

    const createStar = (size: Size): Star => ({
      x: randomBetween(0, size.width),
      y: randomBetween(0, size.height),
      radius: randomBetween(0.5, 1.6),
      alpha: randomBetween(0.35, 0.9),
      phase: randomBetween(0, TWO_PI),
      speed: randomBetween(0.6, 1.8),
    });

    const resetShootingStar = (shootingStar: ShootingStar, size: Size) => {
      const angle = (randomBetween(25, 40) * Math.PI) / 180;
      const fromTop = Math.random() > 0.38;
      shootingStar.active = true;
      shootingStar.x = fromTop
        ? randomBetween(0, size.width * 1.05)
        : randomBetween(size.width * 0.68, size.width + 60);
      shootingStar.y = fromTop
        ? randomBetween(-36, size.height * 0.16)
        : randomBetween(-28, size.height * 0.34);
      shootingStar.unitX = -Math.cos(angle);
      shootingStar.unitY = Math.sin(angle);
      shootingStar.speed = randomBetween(380, 650);
      shootingStar.age = 0;
      shootingStar.life = randomBetween(0.9, 1.6);
      shootingStar.tail = randomBetween(90, 180);
      shootingStar.lineWidth = randomBetween(1.2, 2);
    };

    const drawStarField = (ctx: CanvasRenderingContext2D, animated: boolean, time: number) => {
      ctx.fillStyle = starColor;
      for (let index = 0; index < stars.length; index += 1) {
        const star = stars[index];
        const twinkle = animated ? 0.7 + Math.sin(star.phase + time * star.speed) * 0.3 : 0.82;
        const alpha = star.alpha * twinkle;
        ctx.globalAlpha = isDark ? alpha : alpha * 0.7;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, TWO_PI);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const drawStreak = (
      ctx: CanvasRenderingContext2D,
      shootingStar: ShootingStar,
      alpha: number
    ) => {
      const tailX = shootingStar.x - shootingStar.unitX * shootingStar.tail;
      const tailY = shootingStar.y - shootingStar.unitY * shootingStar.tail;
      const gradient = ctx.createLinearGradient(tailX, tailY, shootingStar.x, shootingStar.y);
      gradient.addColorStop(0, tailColor);
      gradient.addColorStop(1, isDark ? rgba(224, 242, 254, alpha) : rgba(37, 99, 235, alpha));

      ctx.strokeStyle = gradient;
      ctx.lineWidth = shootingStar.lineWidth;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(shootingStar.x, shootingStar.y);
      ctx.stroke();

      ctx.fillStyle = isDark ? 'rgb(224, 242, 254)' : 'rgb(37, 99, 235)';
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(shootingStar.x, shootingStar.y, 1.6, 0, TWO_PI);
      ctx.fill();
      ctx.globalAlpha = 1;
    };

    return {
      setup(_ctx: CanvasRenderingContext2D, size: Size) {
        const starCount = size.width >= 768 ? DESKTOP_STAR_COUNT : MOBILE_STAR_COUNT;
        stars = Array.from({ length: starCount }, () => createStar(size));
        shootingStars = Array.from(
          { length: DESKTOP_SHOOTING_COUNT },
          () => ({ active: false }) as ShootingStar
        );
        elapsedSeconds = 0;
        spawnAccumulator = 0;
        nextSpawnDelay = randomBetween(1.1, 2.5);
      },
      draw(ctx: CanvasRenderingContext2D, size: Size, deltaMs: number) {
        const deltaSeconds = Math.min(deltaMs, MAX_DELTA_MS) / 1000;
        const maxShootingStars = size.width >= 768 ? DESKTOP_SHOOTING_COUNT : MOBILE_SHOOTING_COUNT;
        let activeCount = 0;

        elapsedSeconds += deltaSeconds;
        spawnAccumulator += deltaSeconds;
        ctx.clearRect(0, 0, size.width, size.height);
        drawStarField(ctx, true, elapsedSeconds);

        for (let index = 0; index < maxShootingStars; index += 1) {
          const shootingStar = shootingStars[index];
          if (!shootingStar.active) continue;

          shootingStar.age += deltaSeconds;
          shootingStar.x += shootingStar.unitX * shootingStar.speed * deltaSeconds;
          shootingStar.y += shootingStar.unitY * shootingStar.speed * deltaSeconds;

          if (
            shootingStar.age >= shootingStar.life ||
            shootingStar.x < -shootingStar.tail - 24 ||
            shootingStar.y > size.height + shootingStar.tail + 24
          ) {
            shootingStar.active = false;
            continue;
          }

          activeCount += 1;
          const progress = shootingStar.age / shootingStar.life;
          const fadeIn = Math.min(1, progress / 0.1);
          const fadeOut = progress > 0.75 ? Math.max(0, (1 - progress) / 0.25) : 1;
          drawStreak(ctx, shootingStar, fadeIn * fadeOut);
        }

        if (spawnAccumulator >= nextSpawnDelay && activeCount < maxShootingStars) {
          for (let index = 0; index < maxShootingStars; index += 1) {
            if (!shootingStars[index].active) {
              resetShootingStar(shootingStars[index], size);
              break;
            }
          }
          spawnAccumulator = 0;
          nextSpawnDelay = randomBetween(1.1, 2.5);
        }
      },
      drawStatic(ctx: CanvasRenderingContext2D, size: Size) {
        const frozenStar = shootingStars[0];
        ctx.clearRect(0, 0, size.width, size.height);
        drawStarField(ctx, false, 0);
        if (!frozenStar) return;

        frozenStar.x = size.width * 0.78;
        frozenStar.y = Math.max(32, size.height * 0.22);
        frozenStar.unitX = -Math.cos((32 * Math.PI) / 180);
        frozenStar.unitY = Math.sin((32 * Math.PI) / 180);
        frozenStar.tail = Math.min(150, Math.max(92, size.width * 0.22));
        frozenStar.lineWidth = 1.5;
        drawStreak(ctx, frozenStar, 0.82);
      },
    };
  }, [tone]);

  useCanvasLoop(canvasRef, renderer, activity);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
};
